import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getSupplierGroups, getNonCompliantSuppliersByType, sendComplianceReminders } from '../../../models/suppliersModel';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

function SupplierCompliance() {
  const [activeTab, setActiveTab] = useState('COIDA');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coidaRequiredGroups, setCoidaRequiredGroups] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  
  // Compliance type info
  const complianceInfo = {
    COIDA: {
      label: 'COIDA Compliance',
      description: 'Compensation for Occupational Injuries and Diseases Act',
      icon: '🛡️',
      required: 'For specified services',
      defaultSubject: 'COIDA Certificate Compliance Reminder',
      defaultMessage: `Dear {supplier_name},

This is a reminder regarding your COIDA (Compensation for Occupational Injuries and Diseases Act) certificate compliance.

Our records indicate that your COIDA certificate is either missing, expired, or expiring soon. As a supplier providing services that require COIDA certification, we kindly request that you update your certificate information as soon as possible.

Please use the supplier portal to update your COIDA certificate information: https://portal.silulumanzi.com/supplier

Thank you for your prompt attention to this matter.

Best regards,
Silulumanzi Procurement Team`
    },
    BEE: {
      label: 'BEE Compliance',
      description: 'Broad-Based Black Economic Empowerment',
      icon: '🤝',
      required: 'Always Required',
      defaultSubject: 'BEE Certificate Compliance Reminder',
      defaultMessage: `Dear {supplier_name},

This is a reminder regarding your BEE (Broad-Based Black Economic Empowerment) certificate compliance.

Our records indicate that your BEE certificate is either missing or has expired. As a registered supplier, maintaining valid BEE certification is mandatory.

Please use the supplier portal to update your BEE certificate information: https://portal.silulumanzi.com/supplier

Thank you for your prompt attention to this matter.

Best regards,
Silulumanzi Procurement Team`
    },
    TAX: {
      label: 'Tax Clearance',
      description: 'Tax Compliance Certificate',
      icon: '📋',
      required: 'Always Required',
      defaultSubject: 'Tax Clearance Compliance Reminder',
      defaultMessage: `Dear {supplier_name},

This is a reminder regarding your Tax Clearance certificate compliance.

Our records indicate that your Tax Clearance certificate is either missing or has expired. As a registered supplier, maintaining valid Tax Clearance is mandatory.

Please use the supplier portal to update your Tax Clearance certificate information: https://portal.silulumanzi.com/supplier

Thank you for your prompt attention to this matter.

Best regards,
Silulumanzi Procurement Team`
    }
  };
  
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');

  // Fetch supplier groups and identify which require COIDA
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await getSupplierGroups();
        if (response.status === 'success' && response.data) {
          setSupplierGroups(response.data);
          
          // Filter groups that require COIDA
          const coidaGroups = response.data
            .filter(group => group.Requires_COIDA === 1)
            .map(group => group.Supplier_Group_Code);
          
          setCoidaRequiredGroups(coidaGroups);
        }
      } catch (error) {
        console.error('Error fetching supplier groups:', error);
        toast.error('Failed to fetch supplier groups');
      }
    };
    fetchGroups();
  }, []);

  // Fetch suppliers that have COIDA-required services
  const fetchSuppliers = useCallback(async () => {
    // For BEE and TAX, fetch all suppliers. For COIDA, check if groups are required
    if (activeTab === 'COIDA' && coidaRequiredGroups.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getNonCompliantSuppliersByType(activeTab, '1');

      if (response.status === 'success' && response.data) {
        let filteredSuppliers = response.data;
        
        // Apply global filter
        if (globalFilter && globalFilter.trim()) {
          const searchLower = globalFilter.toLowerCase();
          filteredSuppliers = filteredSuppliers.filter(supplier => {
            return (
              supplier.BP_Code?.toString().toLowerCase().includes(searchLower) ||
              supplier.BP_Name?.toLowerCase().includes(searchLower) ||
              supplier.Contact_Person?.toLowerCase().includes(searchLower) ||
              supplier.E_Mail?.toLowerCase().includes(searchLower)
            );
          });
        }

        setSuppliers(filteredSuppliers);
        setRowCount(filteredSuppliers.length);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error(`Failed to fetch ${activeTab} non-compliant suppliers`);
    } finally {
      setLoading(false);
    }
  }, [activeTab, coidaRequiredGroups, globalFilter]);

  useEffect(() => {
    // Reset search when tab changes
    setGlobalFilter('');
  }, [activeTab]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Memoize columns based on active tab
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'BP_Code',
        header: 'BP Code',
        cell: info => <span className="font-medium text-gray-800">{info.getValue()}</span>,
        size: 100,
      },
      {
        accessorKey: 'BP_Name',
        header: 'Company Name',
        cell: info => <span className="text-gray-700">{info.getValue()}</span>,
        size: 200,
      },
    ];

    if (activeTab === 'COIDA') {
      return [
        ...baseColumns,
        {
          accessorKey: 'Group_Code',
          header: 'COIDA Required Services',
          cell: info => {
            const groupCode = info.getValue();
            if (!groupCode) return <span className="text-gray-400 text-xs">N/A</span>;
            
            const supplierGroups = groupCode.split(',').map(s => s.trim());
            const coidaServices = supplierGroups.filter(group => coidaRequiredGroups.includes(group));
            
            if (coidaServices.length === 0) {
              return <span className="text-gray-400 text-xs">N/A</span>;
            }
            
            return (
              <div className="flex flex-wrap gap-1">
                {coidaServices.slice(0, 2).map((code, idx) => (
                  <span 
                    key={idx} 
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    title={code}
                    style={{ backgroundColor: 'rgba(78, 173, 226, 0.15)', color: 'var(--steel-blue)' }}
                  >
                    {code}
                  </span>
                ))}
                {coidaServices.length > 2 && (
                  <span 
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                    title={coidaServices.slice(2).join(', ')}
                  >
                    +{coidaServices.length - 2} more
                  </span>
                )}
              </div>
            );
          },
          size: 200,
        },
        {
          accessorKey: 'COIDA_Certificate_Number',
          header: 'Certificate #',
          cell: info => {
            const certNumber = info.getValue();
            return certNumber ? (
              <span className="text-gray-700 text-sm">{certNumber}</span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Missing
              </span>
            );
          },
          size: 150,
        },
        {
          accessorKey: 'COIDA_Certificate_Expiry_Date',
          header: 'Expiry Date',
          cell: info => {
            const expiryDate = info.getValue();
            if (!expiryDate) {
              return <span className="text-gray-400 text-xs">N/A</span>;
            }
            
            const today = new Date();
            const expiry = new Date(expiryDate);
            const isExpired = expiry < today;
            const isExpiringSoon = !isExpired && (expiry - today) / (1000 * 60 * 60 * 24) < 30;
            
            return (
              <div className="flex items-center gap-2">
                <span className="text-gray-700 text-sm">{expiryDate}</span>
                {isExpired && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Expired
                  </span>
                )}
                {isExpiringSoon && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Expiring Soon
                  </span>
                )}
              </div>
            );
          },
          size: 200,
        },
      ];
    } else if (activeTab === 'BEE') {
      return [
        ...baseColumns,
        {
          accessorKey: 'BBBEE_Level',
          header: 'BEE Level',
          cell: info => {
            const level = info.getValue();
            return level ? (
              <span className="text-gray-700 text-sm font-medium">{level}</span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Missing
              </span>
            );
          },
          size: 120,
        },
        {
          accessorKey: 'BBBEE_Type',
          header: 'BEE Type',
          cell: info => {
            const type = info.getValue();
            return type ? (
              <span className="text-gray-700 text-sm">{type}</span>
            ) : (
              <span className="text-gray-400 text-xs">N/A</span>
            );
          },
          size: 120,
        },
        {
          accessorKey: 'BBBEE_Expiration_Date',
          header: 'Expiry Date',
          cell: info => {
            const expiryDate = info.getValue();
            if (!expiryDate) {
              return <span className="text-gray-400 text-xs">N/A</span>;
            }
            
            const today = new Date();
            const expiry = new Date(expiryDate);
            const isExpired = expiry < today;
            
            return (
              <div className="flex items-center gap-2">
                <span className="text-gray-700 text-sm">{expiryDate}</span>
                {isExpired && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Expired
                  </span>
                )}
              </div>
            );
          },
          size: 180,
        },
      ];
    } else if (activeTab === 'TAX') {
      return [
        ...baseColumns,
        {
          accessorKey: 'Tax_Certification_Number',
          header: 'Tax #',
          cell: info => {
            const certNumber = info.getValue();
            return certNumber ? (
              <span className="text-gray-700 text-sm">{certNumber}</span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Missing
              </span>
            );
          },
          size: 150,
        },
        {
          accessorKey: 'Tax_Certificate_Expiry_Date',
          header: 'Expiry Date',
          cell: info => {
            const expiryDate = info.getValue();
            if (!expiryDate) {
              return <span className="text-gray-400 text-xs">N/A</span>;
            }
            
            const today = new Date();
            const expiry = new Date(expiryDate);
            const isExpired = expiry < today;
            
            return (
              <div className="flex items-center gap-2">
                <span className="text-gray-700 text-sm">{expiryDate}</span>
                {isExpired && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Expired
                  </span>
                )}
              </div>
            );
          },
          size: 180,
        },
      ];
    }

    return baseColumns;
  }, [activeTab, coidaRequiredGroups]);

  // Handle opening reminder modal
  const handleOpenReminderModal = () => {
    if (suppliers.length === 0) {
      toast.info(`All suppliers are compliant with ${complianceInfo[activeTab].label}`);
      return;
    }
    
    setEmailSubject(complianceInfo[activeTab].defaultSubject);
    setEmailMessage(complianceInfo[activeTab].defaultMessage);
    setShowReminderModal(true);
  };

  // Handle sending reminders
  const handleSendReminders = async () => {
    const bpCodes = suppliers.map(supplier => supplier.BP_Code);
    
    if (bpCodes.length === 0) {
      toast.error(`No non-compliant suppliers to send reminders to`);
      return;
    }
    
    try {
      setSending(true);
      const response = await sendComplianceReminders(bpCodes, activeTab, emailSubject, emailMessage);
      
      if (response.status === 'success') {
        toast.success(response.message);
        setShowReminderModal(false);
      } else {
        toast.error(response.message || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const table = useReactTable({
    data: suppliers,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(78, 173, 226, 0.15)' }}>
                <svg className="h-5 w-5" style={{ color: 'var(--steel-blue)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Supplier Compliance</h1>
                <p className="text-xs text-gray-500 mb-1">Monitor suppliers' compliance across multiple certifications</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: 'var(--steel-blue)' }}>
                    {rowCount} supplier{rowCount !== 1 ? 's' : ''} require{rowCount === 1 ? 's' : ''} attention
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 items-end">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenReminderModal}
                  disabled={loading || suppliers.length === 0}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white disabled:opacity-50"
                  style={{ backgroundColor: 'var(--steel-blue)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--picton-blue)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--steel-blue)'}
                  title="Send Reminders"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Reminders
                </button>
                
                <button
                  onClick={fetchSuppliers}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 disabled:opacity-50"
                  style={{ '--tw-ring-color': 'var(--steel-blue)' }}
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={globalFilter ?? ''}
                  onChange={e => setGlobalFilter(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 sm:text-sm"
                  style={{ '--tw-ring-color': 'var(--steel-blue)', '--tw-border-color': 'var(--steel-blue)' }}
                  placeholder="Search suppliers..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-2">
          <div className="flex border-b border-gray-200">
            {['COIDA', 'BEE', 'TAX'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 text-white'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
                style={{
                  backgroundColor: activeTab === tab ? 'var(--steel-blue)' : 'transparent',
                  borderBottomColor: activeTab === tab ? 'var(--steel-blue)' : 'transparent',
                }}
              >
                <span className="mr-2">{complianceInfo[tab].icon}</span>
                {complianceInfo[tab].label}
                <span className="text-xs ml-2 opacity-75">
                  {tab === 'COIDA' ? '(Service-based)' : '(Always Required)'}
                </span>
              </button>
            ))}
          </div>

          {/* Tab Content Info */}
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <p className="text-xs text-gray-600">
              <strong>{complianceInfo[activeTab].description}</strong> • 
              <span className="ml-2">Required: {complianceInfo[activeTab].required}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4">
        {loading ? (
          <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
            <div className="flex justify-center items-center space-x-2">
              <svg className="animate-spin h-5 w-5" style={{ color: 'var(--steel-blue)' }} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading suppliers...</span>
            </div>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-gray-500 mt-2">All suppliers are compliant with {complianceInfo[activeTab].label}.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead style={{ backgroundColor: 'rgba(78, 173, 226, 0.08)' }}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="px-3 py-2 text-left text-xs font-semibold uppercase"
                          style={{ color: 'var(--steel-blue)' }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="transition-colors" style={{ '--hover-bg': 'rgba(78, 173, 226, 0.05)' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(78, 173, 226, 0.05)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-3 py-2 text-sm text-gray-700">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Record Count */}
            {suppliers.length > 0 && (
              <div className="px-3 py-3 border-t border-gray-200 flex items-center justify-between" style={{ backgroundColor: 'rgba(147, 51, 234, 0.02)' }}>
                <div className="text-xs text-gray-500">
                  Displaying {rowCount} supplier{rowCount !== 1 ? 's' : ''} requiring attention
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reminder Email Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Modal Header */}
          <div className="px-8 py-6 border-b border-gray-200 rounded-t-xl" style={{ background: 'linear-gradient(135deg, var(--steel-blue), var(--picton-blue))' }}>
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send {complianceInfo[activeTab].label} Reminders
            </h3>
            <p className="text-sm mt-1" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              Send reminder emails to suppliers with compliance issues
            </p>
          </div>
            
            {/* Modal Body */}
            <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
              {/* Recipients Summary */}
              <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(78, 173, 226, 0.08)', borderColor: 'var(--picton-blue)', borderWidth: '1px' }}>
                <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--steel-blue)' }}>Recipients ({suppliers.length} suppliers)</h4>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="text-sm space-y-1" style={{ color: 'var(--text-primary)' }}>
                    {suppliers.map((supplier, idx) => (
                      <li key={idx} className="flex items-center">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: 'var(--color-success)' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">{supplier.BP_Name}</span>
                        <span className="mx-2" style={{ color: 'var(--ash-gray)' }}>•</span>
                        <span className="text-xs">{supplier.E_Mail || 'No email'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSendReminders(); }} className="space-y-6">
                <div>
                  <label htmlFor="subject" className="block text-sm font-bold text-gray-800 mb-2">
                    Email Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 text-gray-900 placeholder-gray-500 text-base"
                    style={{ '--tw-ring-color': 'var(--steel-blue)', '--tw-border-color': 'var(--steel-blue)' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'var(--steel-blue)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    placeholder="Enter email subject"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-800 mb-2">
                    Email Message <span className="text-red-500">*</span>
                  </label>
                  <div className="mb-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    <strong>Available placeholders:</strong> {'{supplier_name}'}, {'{bp_code}'}, {'{contact_person}'}
                  </div>
                  <textarea
                    id="message"
                    rows={12}
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 text-gray-900 placeholder-gray-500 text-sm leading-relaxed resize-none font-mono"
                    style={{ '--tw-ring-color': 'var(--steel-blue)', '--tw-border-color': 'var(--steel-blue)' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'var(--steel-blue)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    placeholder="Enter the email message..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    The message will be personalized for each supplier using the placeholders
                  </p>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowReminderModal(false)}
                    disabled={sending}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sending}
                    className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                    style={{ backgroundColor: 'var(--steel-blue)', '--tw-ring-color': 'var(--steel-blue)' }}
                    onMouseEnter={(e) => !sending && (e.currentTarget.style.backgroundColor = 'var(--picton-blue)')}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--steel-blue)'}
                  >
                    {sending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Reminders
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierCompliance;

